/**
 * BookingContext Component
 *
 * Shows booking details in the message sidebar.
 */

import React from 'react';
import { Link } from 'react-router-dom';

interface BookingContextProps {
  booking: {
    id: string;
    status: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
    checkIn: Date;
    checkOut: Date;
    guests: number;
    totalPrice: number;
    hostPayout: number;
  };
  onAccept?: () => void;
  onDecline?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  pending: { label: 'Pending Approval', bg: 'bg-amber-100', color: 'text-amber-700' },
  confirmed: { label: 'Confirmed', bg: 'bg-green-100', color: 'text-green-700' },
  active: { label: 'Guest Checked In', bg: 'bg-blue-100', color: 'text-blue-700' },
  completed: { label: 'Completed', bg: 'bg-gray-100', color: 'text-gray-600' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100', color: 'text-red-600' },
};

export const BookingContext: React.FC<BookingContextProps> = ({
  booking,
  onAccept,
  onDecline,
}) => {
  const status = STATUS_CONFIG[booking.status];
  const nights = Math.ceil(
    (booking.checkOut.getTime() - booking.checkIn.getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900">Booking</h4>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
          {status.label}
        </span>
      </div>

      <div className="space-y-2 text-sm mb-4">
        <div className="flex justify-between">
          <span className="text-gray-500">Check-in</span>
          <span className="text-gray-900">
            {booking.checkIn.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Check-out</span>
          <span className="text-gray-900">
            {booking.checkOut.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Duration</span>
          <span className="text-gray-900">
            {nights} {nights === 1 ? 'night' : 'nights'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Guests</span>
          <span className="text-gray-900">{booking.guests}</span>
        </div>
        <div className="flex justify-between pt-2 border-t">
          <span className="text-gray-700 font-medium">Your payout</span>
          <span className="text-green-600 font-semibold">${booking.hostPayout}</span>
        </div>
      </div>

      {/* Actions for pending bookings */}
      {booking.status === 'pending' && onAccept && onDecline && (
        <div className="flex gap-2 mb-3">
          <button
            onClick={onDecline}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Decline
          </button>
          <button
            onClick={onAccept}
            className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
          >
            Accept
          </button>
        </div>
      )}

      <Link
        to={`/host/bookings/${booking.id}`}
        className="block text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
      >
        View booking details →
      </Link>
    </div>
  );
};

export default BookingContext;
